import { useState } from 'react';
import {
  ArrowLeft,
  Download,
  FileText,
  CheckCircle2,
  Target,
  MessageSquare,
  Users,
  Calendar,
  Clock,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Wand2,
  Loader2,
  Mail
} from 'lucide-react';
import type { MeetingDetailResponse } from '../types';
import { EmailModal } from './EmailModal';

interface MeetingDetailProps {
  data: MeetingDetailResponse;
  onBack: () => void;
  onExport: (format: 'pdf' | 'docx', includeTranscript: boolean) => void;
  onGenerateAnalysis: () => void;
  onReanalyze: () => void;
  onSendEmail: (contentType: 'full' | 'summary', recipientType: string, customRecipients?: string[]) => Promise<void>;
  isLoading: boolean;
}

/**
 * MeetingDetail Component
 * Toplantı detaylarını, transkripti ve analizi gösterir
 */
export function MeetingDetail({ data, onBack, onExport, onGenerateAnalysis, onReanalyze, onSendEmail, isLoading }: MeetingDetailProps) {
  // Varsayılan olarak Transkript sekmesi açık
  const [activeTab, setActiveTab] = useState<'summary' | 'transcript'>('transcript');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    keyPoints: true,
    decisions: true,
    actionItems: true,
    topics: true,
  });

  const { meeting, transcript, analysis } = data;

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '-';
    // UTC ISO string'i parse et
    const date = new Date(dateString);
    // Eğer tarih geçerli değilse
    if (isNaN(date.getTime())) return '-';

    // Manuel Türkiye saati (+3 saat UTC offset)
    const turkeyDate = new Date(date.getTime() + (3 * 60 * 60 * 1000));
    return turkeyDate.toLocaleString('tr-TR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateDuration = () => {
    const start = new Date(meeting.startDateTime);
    const end = new Date(meeting.endDateTime);
    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.round(diffMs / 60000);
    
    if (diffMins < 60) return `${diffMins} dakika`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return mins > 0 ? `${hours} saat ${mins} dakika` : `${hours} saat`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-lg border-b border-slate-200/50 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-slate-600 hover:text-indigo-600 transition-colors"
            >
              <ArrowLeft size={20} />
              <span>Geri</span>
            </button>

            <div className="flex items-center gap-2">
              {analysis && (
                <button
                  onClick={onReanalyze}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-xl transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
                  <span>Yeniden Analiz</span>
                </button>
              )}

              <button
                onClick={() => setShowEmailModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all shadow-lg shadow-blue-500/25"
              >
                <Mail size={16} />
                <span>Email Gönder</span>
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg shadow-indigo-500/25"
                >
                  <Download size={16} />
                  <span>Dışa Aktar</span>
                </button>

                {showExportMenu && (
                  <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-20">
                    <button
                      onClick={() => { onExport('pdf', false); setShowExportMenu(false); }}
                      className="w-full px-4 py-2.5 text-left hover:bg-slate-50 text-sm text-slate-700"
                    >
                      📄 PDF olarak indir
                    </button>
                    <button
                      onClick={() => { onExport('pdf', true); setShowExportMenu(false); }}
                      className="w-full px-4 py-2.5 text-left hover:bg-slate-50 text-sm text-slate-700"
                    >
                      📄 PDF (Transkript dahil)
                    </button>
                    <div className="border-t border-slate-100 my-1" />
                    <button
                      onClick={() => { onExport('docx', false); setShowExportMenu(false); }}
                      className="w-full px-4 py-2.5 text-left hover:bg-slate-50 text-sm text-slate-700"
                    >
                      📝 Word olarak indir
                    </button>
                    <button
                      onClick={() => { onExport('docx', true); setShowExportMenu(false); }}
                      className="w-full px-4 py-2.5 text-left hover:bg-slate-50 text-sm text-slate-700"
                    >
                      📝 Word (Transkript dahil)
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Meeting Info */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/50 p-6 mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">{meeting.subject}</h1>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2 text-slate-600">
              <Calendar size={16} className="text-slate-400" />
              <span>{formatDateTime(meeting.startDateTime)}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Clock size={16} className="text-slate-400" />
              <span>{calculateDuration()}</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Users size={16} className="text-slate-400" />
              <span>{meeting.participants.length} katılımcı</span>
            </div>
            <div className="text-slate-600">
              <span className="text-slate-400">Organizatör:</span> {meeting.organizer.displayName}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/50 overflow-hidden">
          <div className="border-b border-slate-200">
            <nav className="flex">
              <button
                onClick={() => setActiveTab('transcript')}
                className={`px-6 py-4 text-sm font-medium transition-all ${
                  activeTab === 'transcript'
                    ? 'border-b-2 border-indigo-600 text-indigo-600 bg-indigo-50/50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span className="flex items-center gap-2">
                  <FileText size={16} />
                  Transkript
                </span>
              </button>
              <button
                onClick={() => setActiveTab('summary')}
                className={`px-6 py-4 text-sm font-medium transition-all ${
                  activeTab === 'summary'
                    ? 'border-b-2 border-indigo-600 text-indigo-600 bg-indigo-50/50'
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <span className="flex items-center gap-2">
                  <Sparkles size={16} />
                  Özet & Analiz
                  {analysis && (
                    <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">
                      Hazır
                    </span>
                  )}
                </span>
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* TRANSKRIPT SEKMESİ */}
            {activeTab === 'transcript' && (
              <div>
                {transcript && transcript.entries.length > 0 ? (
                  <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                    {transcript.entries.map((entry, index) => (
                      <div key={index} className="flex gap-4 group hover:bg-slate-50 rounded-lg p-2 -m-2 transition-colors">
                        <div className="flex-shrink-0 w-24 text-xs text-slate-400 pt-1 font-mono">
                          {entry.timestamp}
                        </div>
                        <div className="flex-1">
                          <span className="font-semibold text-indigo-600">{entry.speaker}:</span>{' '}
                          <span className="text-slate-700">{entry.text}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FileText size={40} className="text-slate-400" />
                    </div>
                    <p className="text-slate-600 font-medium text-lg">Bu toplantı için transkript bulunmuyor</p>
                    <p className="text-slate-400 text-sm mt-2">
                      Toplantıda transkript özelliği açık olmalıdır.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* ÖZET & ANALİZ SEKMESİ */}
            {activeTab === 'summary' && analysis ? (
              <div className="space-y-6">
                {/* Özet */}
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <MessageSquare size={20} className="text-indigo-600" />
                    Özet
                  </h3>
                  <p className="text-slate-700 leading-relaxed whitespace-pre-wrap bg-slate-50 rounded-xl p-4">
                    {analysis.summary}
                  </p>
                </div>

                {/* Önemli Noktalar */}
                {analysis.keyPoints.length > 0 && (
                  <div>
                    <button
                      onClick={() => toggleSection('keyPoints')}
                      className="w-full flex items-center justify-between text-lg font-semibold text-slate-900 mb-3"
                    >
                      <span className="flex items-center gap-2">
                        <Target size={20} className="text-emerald-500" />
                        Önemli Noktalar ({analysis.keyPoints.length})
                      </span>
                      {expandedSections.keyPoints ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    {expandedSections.keyPoints && (
                      <ul className="space-y-2">
                        {analysis.keyPoints.map((point, index) => (
                          <li key={index} className="flex items-start gap-3 text-slate-700">
                            <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-sm font-medium">
                              {index + 1}
                            </span>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Alınan Kararlar */}
                {analysis.decisions.length > 0 && (
                  <div>
                    <button
                      onClick={() => toggleSection('decisions')}
                      className="w-full flex items-center justify-between text-lg font-semibold text-slate-900 mb-3"
                    >
                      <span className="flex items-center gap-2">
                        <CheckCircle2 size={20} className="text-blue-500" />
                        Alınan Kararlar ({analysis.decisions.length})
                      </span>
                      {expandedSections.decisions ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    {expandedSections.decisions && (
                      <ul className="space-y-2">
                        {analysis.decisions.map((decision, index) => (
                          <li key={index} className="flex items-start gap-3 text-slate-700">
                            <CheckCircle2 size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
                            <span>{decision}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {/* Aksiyon Maddeleri */}
                {analysis.actionItems.length > 0 && (
                  <div>
                    <button
                      onClick={() => toggleSection('actionItems')}
                      className="w-full flex items-center justify-between text-lg font-semibold text-slate-900 mb-3"
                    >
                      <span className="flex items-center gap-2">
                        <FileText size={20} className="text-orange-500" />
                        Aksiyon Maddeleri ({analysis.actionItems.length})
                      </span>
                      {expandedSections.actionItems ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    {expandedSections.actionItems && (
                      <div className="space-y-3">
                        {analysis.actionItems.map((item) => (
                          <div key={item.id} className="bg-orange-50 border border-orange-100 rounded-xl p-4">
                            <p className="text-slate-800 font-medium">{item.description}</p>
                            <div className="flex gap-4 mt-2 text-sm text-slate-600">
                              {item.assignee && (
                                <span>Sorumlu: <strong>{item.assignee}</strong></span>
                              )}
                              {item.dueDate && (
                                <span>Son Tarih: <strong>{item.dueDate}</strong></span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Konular */}
                {analysis.topics && analysis.topics.length > 0 && (
                  <div>
                    <button
                      onClick={() => toggleSection('topics')}
                      className="w-full flex items-center justify-between text-lg font-semibold text-slate-900 mb-3"
                    >
                      <span className="flex items-center gap-2">
                        <MessageSquare size={20} className="text-purple-500" />
                        Toplantı Konuları ({analysis.topics.length})
                      </span>
                      {expandedSections.topics ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    {expandedSections.topics && (
                      <div className="space-y-2">
                        {analysis.topics.map((topic, index) => (
                          <div key={index} className="bg-purple-50 border border-purple-100 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-slate-800 font-medium">{topic.name}</span>
                              <span className="text-purple-600 font-bold text-lg">{topic.percentage || 0}%</span>
                            </div>
                            {/* Progress bar */}
                            <div className="w-full bg-purple-200 rounded-full h-2">
                              <div
                                className="bg-gradient-to-r from-purple-500 to-indigo-500 h-2 rounded-full transition-all duration-500"
                                style={{ width: `${topic.percentage || 0}%` }}
                              />
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              {topic.mentionCount} kez bahsedildi
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : activeTab === 'summary' && !analysis ? (
              /* ÖZET YOK - ÖZET ÇIKAR BUTONU */
              <div className="text-center py-16">
                <div className="relative inline-block mb-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-3xl flex items-center justify-center mx-auto">
                    <Wand2 size={48} className="text-indigo-600" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                    <Sparkles size={16} className="text-white" />
                  </div>
                </div>
                
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  AI ile Özet Oluştur
                </h3>
                <p className="text-slate-500 mb-8 max-w-md mx-auto">
                  Transkripti analiz ederek toplantı özeti, önemli noktalar, 
                  alınan kararlar ve aksiyon maddelerini otomatik olarak çıkaralım.
                </p>
                
                {transcript && transcript.entries.length > 0 ? (
                  <button
                    onClick={onGenerateAnalysis}
                    disabled={isLoading}
                    className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-xl shadow-indigo-500/25 font-medium text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={24} className="animate-spin" />
                        <span>Analiz ediliyor...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles size={24} />
                        <span>Özet Çıkar</span>
                      </>
                    )}
                  </button>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 max-w-md mx-auto">
                    <p className="text-amber-800 text-sm">
                      ⚠️ Özet oluşturmak için önce transkript gereklidir.
                    </p>
                  </div>
                )}

                {isLoading && (
                  <p className="text-slate-400 text-sm mt-4 animate-pulse">
                    Bu işlem birkaç saniye sürebilir...
                  </p>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <EmailModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          onSend={onSendEmail}
          meetingData={data}
        />
      )}
    </div>
  );
}
